import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSesion } from '../estado/SesionContext';
import type { ResumenCurso } from '../domain/types';

/**
 * Las evaluaciones, separadas del curso.
 *
 * Existe como pestaña propia porque quien ya leyó las lecciones no quiere volver a
 * recorrerlas para reintentar el quiz: quiere ir directo. Un curso que todavía no se
 * empezó manda al curso, no al quiz — rendir sin haber leído no mide nada.
 */
export function Quizzes() {
  const { repo, espacio } = useSesion();
  const [resumen, setResumen] = useState<ResumenCurso[] | null>(null);

  useEffect(() => {
    if (!repo) return;
    let vivo = true;
    void repo.listarResumen(espacio.id).then((r) => {
      if (vivo) setResumen(r);
    });
    return () => {
      vivo = false;
    };
  }, [repo, espacio.id]);

  return (
    <div>
      <Link to="/" className="text-sm font-bold text-azul">
        ← Inicio
      </Link>

      <header className="mb-5 mt-3">
        <h1 className="font-titulos text-2xl font-extrabold">Quizzes</h1>
        <p className="text-sm text-gris">Tus evaluaciones disponibles</p>
      </header>

      {resumen === null ? (
        <p className="py-8 text-center text-gris">Cargando…</p>
      ) : (
        <div className="space-y-3">
          {resumen.map((r) => (
            <FilaQuiz key={r.curso.id} resumen={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilaQuiz({ resumen }: { resumen: ResumenCurso }) {
  const { curso, aprobado, mejorNota, porcentajeAvance } = resumen;
  const bloqueado = curso.estado === 'proximamente';
  const empezado = porcentajeAvance > 0 || mejorNota !== null;
  const preguntas = curso.preguntas.length || curso.preguntasPrevistas || 0;

  return (
    <article
      className={`flex items-center gap-4 rounded-2xl border border-borde bg-white p-4 ${
        bloqueado ? 'opacity-60' : ''
      }`}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cielo text-2xl">
        {curso.emoji}
      </span>

      <div className="min-w-0 flex-1">
        <h3 className="font-extrabold leading-tight">Quiz · {curso.titulo}</h3>
        <p className="text-xs text-gris">
          {preguntas} preguntas · nota mínima {curso.notaMinima}
          {bloqueado ? (
            ' · disponible próximamente'
          ) : aprobado ? (
            <>
              {' · '}
              <b className="text-verde">Aprobado con {mejorNota}</b>
            </>
          ) : mejorNota !== null ? (
            <>
              {' · '}
              <b className="text-rojo">Última nota: {mejorNota} — reintenta</b>
            </>
          ) : null}
        </p>
      </div>

      {bloqueado ? (
        <span className="shrink-0 text-lg" aria-label="Bloqueado">
          🔒
        </span>
      ) : (
        // Sin haber leído nada, el botón manda al curso: rendir sin leer no mide nada.
        <Link
          to={empezado ? `/curso/${curso.id}/quiz` : `/curso/${curso.id}`}
          className="shrink-0 rounded-full bg-rojo px-4 py-2 text-xs font-extrabold text-white transition hover:bg-rojo-osc"
        >
          {empezado ? 'RENDIR' : 'IR AL CURSO'}
        </Link>
      )}
    </article>
  );
}
